import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SearchFieldSetting from 'components/kea/SearchFieldSetting';
import SaveActionBtnGrp from 'components/kea/settings/SaveActionBtnGrp';
import IntlMessages from 'util/IntlMessages';
import { useLocalizedMessage } from 'util/useLocalizedMessage';
import SearchFeaturePageShell from 'components/shell/SearchFeaturePageShell';
import IndexingPageGuard from 'features/operation-supervisor/IndexingPageGuard';
import { useShellAppContext } from 'features/shell/context/useShellAppContext';
import {
  actions as fieldsActions,
  selectAllFields,
  selectLoadError,
  selectHasChanges,
  selectIsFetching,
  selectIsSaving,
  loadFields,
  saveFields,
} from 'slices/FieldsSlice';
import styles from './Fields.module.scss';

const VISIBLE_FIELD_ORDER = [
  'name',
  'params',
  'tags',
  'vendor',
  'model',
  'description',
  'smartContext',
  'vendorCode',
  'id',
  'groupId',
];

const VISIBLE_FIELD_RANK = new Map(
  VISIBLE_FIELD_ORDER.map((fieldId, index) => [fieldId, index]),
);

const Fields = () => {
  const dispatch = useDispatch();
  const { capabilities, compatibility } = useShellAppContext();
  const localizeMessage = useLocalizedMessage();
  const { currentShop } = useSelector((state) => state.app);
  const fields = useSelector(selectAllFields);
  const hasChanges = useSelector(selectHasChanges);
  const isFetching = useSelector(selectIsFetching);
  const isSaving = useSelector(selectIsSaving);
  const loadError = useSelector(selectLoadError);
  const deferredReindexEnabled = compatibility?.catalogUpdates?.deferred
    ?? capabilities?.deferredReindexV1 === true;

  React.useEffect(() => {
    if (!currentShop) return;
    dispatch(loadFields(currentShop));
  }, [dispatch, currentShop]);

  // Autocomplete is not supported by the bridge transport — bridge returns autocomplete=null.
  // Once the first batch of fields loads, we check whether the transport provides the concept.
  const supportsAutocomplete = fields.length > 0 && fields[0].autocomplete !== null;
  const orderedFields = React.useMemo(() => fields
    .filter((field) => VISIBLE_FIELD_RANK.has(field.id))
    .sort((left, right) => VISIBLE_FIELD_RANK.get(left.id) - VISIBLE_FIELD_RANK.get(right.id)), [fields]);

  const weightChangeHandler = (id, value) =>
    dispatch(fieldsActions.updateField({ id, changes: { weight: value } }));
  const autoCompleteChangeHandler = supportsAutocomplete
    ? (id, value) => dispatch(fieldsActions.updateField({ id, changes: { autocomplete: value } }))
    : null;
  const save = () => dispatch(saveFields({
    shopId: currentShop,
    deferredReindex: deferredReindexEnabled,
  }));
  const discard = () => dispatch(fieldsActions.discardFields());

  const buttons = [
    {
      text: localizeMessage('common.save'), action: save, color: 'primary', testId: 'fields-save',
    },
    {
      text: localizeMessage('common.discard'), action: discard, color: 'secondary', testId: 'fields-discard',
    },
  ];

  return (
    <SearchFeaturePageShell
      pageId="customizationFields"
      isLoading={isFetching && fields.length === 0}
      error={loadError && !isFetching ? loadError : null}
      actions={(
        <SaveActionBtnGrp
          isFetching={isFetching || isSaving}
          buttons={buttons}
          disabled={!hasChanges}
        />
      )}
    >
      <IndexingPageGuard>
        <section className={styles.surface} data-testid="fields-surface">
          <div className={`${styles.columnHeader} ${supportsAutocomplete ? styles.columnHeaderWithAutocomplete : ''}`.trim()}>
            <span><IntlMessages id="search.fields.header.fieldName" /></span>
            <span><IntlMessages id="search.fields.header.priority" /></span>
            {supportsAutocomplete && (
              <span className={styles.autocompleteHeader}>
                <IntlMessages id="search.fields.header.autocomplete" />
              </span>
            )}
          </div>
          {orderedFields.map((field) => (
            <SearchFieldSetting
              key={field.id}
              id={field.id}
              value={field.weight}
              autocomplete={field.autocomplete !== null ? field.autocomplete : false}
              onChangeWeight={weightChangeHandler}
              onChangeAutocomplete={autoCompleteChangeHandler}
              hideAutocomplete={!supportsAutocomplete}
              title={field.title}
            />
          ))}
          {orderedFields.length === 0 && (
            <div className={styles.emptyState}>{localizeMessage('search.fields.empty')}</div>
          )}
        </section>
      </IndexingPageGuard>
    </SearchFeaturePageShell>
  );
};

export default Fields;
